/**
 * @fileoverview Admin configuration for different environments
 * 
 * This file allows easy management of admin emails for different environments
 * and roles (primary admin, reviewers, moderators).
 */

import { getEnvironment } from './environment';

export interface AdminConfig {
  primaryAdmin: string;
  reviewers: string[];
  moderators: string[];
}

export interface AdminRole {
  email: string;
  role: 'primary' | 'reviewer' | 'moderator';
  environment: string;
  addedAt: string;
  addedBy: string;
}

// Admin configuration for different environments
const ADMIN_CONFIGS: Record<string, AdminConfig> = {
  development: {
    primaryAdmin: 'pawanladukar@gmail.com',
    reviewers: [
      'pawanladukar@gmail.com',
      // Add more dev reviewers here
    ],
    moderators: [
      'pawanladukar@gmail.com',
      // Add more dev moderators here
    ]
  },
  production: {
    primaryAdmin: 'pawanladukar@gmail.com',
    reviewers: [
      'pawan@spinnergames.fun',
      'admin@spinnergames.fun',
      // Add more prod reviewers here
    ],
    moderators: [
      'pawan@spinnergames.fun',
      'admin@spinnergames.fun',
      // Add more prod moderators here
    ]
  }
};

/**
 * Get admin configuration for current environment
 */
export function getCurrentAdminConfig(): AdminConfig {
  const environment = getEnvironment();
  return ADMIN_CONFIGS[environment] || ADMIN_CONFIGS.development;
}

/**
 * Get all admin emails for current environment
 */
export function getAllAdminEmails(): string[] {
  const config = getCurrentAdminConfig();
  return [
    config.primaryAdmin,
    ...config.reviewers,
    ...config.moderators
  ].filter((email, index, arr) => arr.indexOf(email) === index); // Remove duplicates
}

/**
 * Check if user is admin in current environment
 */
export function isAdminInEnvironment(email: string): boolean {
  const adminEmails = getAllAdminEmails();
  return adminEmails.includes(email);
}

/**
 * Get user role in current environment
 */
export function getUserRole(email: string): 'primary' | 'reviewer' | 'moderator' | 'none' {
  const config = getCurrentAdminConfig();
  
  if (email === config.primaryAdmin) return 'primary';
  if (config.reviewers.includes(email)) return 'reviewer';
  if (config.moderators.includes(email)) return 'moderator';
  return 'none';
}

/**
 * Add a new admin email to the configuration
 * Note: This is a helper function for future admin management interface
 */
export function addAdminEmail(email: string, role: 'primary' | 'reviewer' | 'moderator', environment?: string): void {
  const env = environment || getEnvironment();
  const config = ADMIN_CONFIGS[env];
  
  if (!config) {
    console.error(`Environment ${env} not found in admin config`);
    return;
  }
  
  switch (role) {
    case 'primary':
      config.primaryAdmin = email;
      break;
    case 'reviewer':
      if (!config.reviewers.includes(email)) {
        config.reviewers.push(email);
      }
      break;
    case 'moderator':
      if (!config.moderators.includes(email)) {
        config.moderators.push(email);
      }
      break;
  }
  
  console.log(`Added ${email} as ${role} admin for ${env} environment`);
}

/**
 * Remove an admin email from the configuration
 * Note: This is a helper function for future admin management interface
 */
export function removeAdminEmail(email: string, role: 'reviewer' | 'moderator', environment?: string): void {
  const env = environment || getEnvironment();
  const config = ADMIN_CONFIGS[env];
  
  if (!config) {
    console.error(`Environment ${env} not found in admin config`);
    return;
  }
  
  switch (role) {
    case 'reviewer':
      config.reviewers = config.reviewers.filter(e => e !== email);
      break;
    case 'moderator':
      config.moderators = config.moderators.filter(e => e !== email);
      break;
  }
  
  console.log(`Removed ${email} as ${role} admin from ${env} environment`);
}

/**
 * Get all admin configurations (for admin management interface)
 */
export function getAllAdminConfigs(): Record<string, AdminConfig> {
  return { ...ADMIN_CONFIGS };
}

/**
 * Export admin emails for Firestore rules
 * This function returns all admin emails that should be included in Firestore security rules
 */
export function getAdminEmailsForFirestoreRules(): string[] {
  const allEmails: string[] = [];
  
  Object.values(ADMIN_CONFIGS).forEach(config => {
    allEmails.push(config.primaryAdmin);
    allEmails.push(...config.reviewers);
    allEmails.push(...config.moderators);
  });
  
  // Remove duplicates and return
  return [...new Set(allEmails)];
}
